import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Hero } from '@/components/sections/Hero';
import { Drops } from '@/components/sections/Drops';
import { Worlds } from '@/components/sections/Worlds';
import { Listen } from '@/components/sections/Listen';
import { Universe } from '@/components/sections/Universe';
import { Footer } from '@/components/sections/Footer';
import { AudioPlayer } from '@/components/sections/AudioPlayer';
import { useReleases } from '@/hooks/useReleases';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import '@/App.css';

const HomePage = () => {
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  
  // Load releases from JSON
  const { 
    albums, 
    eras, 
    streamingLinks, 
    loading, 
    error: dataError,
    getLatestAlbum 
  } = useReleases();
  
  // Audio player hook with Howler.js
  const {
    currentTrack,
    currentAlbum,
    isPlaying,
    isLoading,
    progress,
    duration,
    volume,
    error: audioError,
    playTrack,
    playAlbum,
    togglePlayPause,
    seek,
    updateVolume,
    stop,
    playNext,
    playPrevious,
  } = useAudioPlayer();

  const latestAlbum = getLatestAlbum();

  const handlePlayAlbum = useCallback((album) => {
    playAlbum(album);
    setIsPlayerOpen(true);
    setIsAudioEnabled(true);
  }, [playAlbum]);

  const handleClosePlayer = useCallback(() => {
    stop();
    setIsPlayerOpen(false);
  }, [stop]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-crimson border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-body">Loading SUGO SOUND...</p>
        </div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive font-body">Error loading releases: {dataError}</p>
        </div>
      </div>
    );
  }

  return (
    <main className="relative">
      <Navigation 
        isAudioEnabled={isAudioEnabled} 
        onToggleAudio={() => {
          if (isAudioEnabled && isPlaying) {
            togglePlayPause();
          }
          setIsAudioEnabled(!isAudioEnabled);
        }} 
      />
      <Hero 
        latestAlbum={latestAlbum} 
        onPlayAlbum={handlePlayAlbum} 
      />
      <Drops 
        albums={albums}
        onPlayAlbum={handlePlayAlbum}
        currentlyPlayingAlbum={currentAlbum}
        isPlaying={isPlaying}
      />
      <Worlds 
        eras={eras}
        albums={albums}
        onPlayAlbum={handlePlayAlbum}
      />
      <Listen 
        albums={albums}
        streamingLinks={streamingLinks}
        onPlayAlbum={handlePlayAlbum}
      />
      <Universe />
      <Footer albums={albums} />
      
      {/* Persistent Audio Player */}
      <AudioPlayer
        isOpen={isPlayerOpen}
        onClose={handleClosePlayer}
        currentTrack={currentTrack}
        currentAlbum={currentAlbum}
        isPlaying={isPlaying}
        isLoading={isLoading}
        progress={progress}
        duration={duration}
        volume={volume}
        error={audioError}
        onTogglePlayPause={togglePlayPause}
        onSeek={seek}
        onVolumeChange={updateVolume}
        onNext={playNext}
        onPrevious={playPrevious}
      />
      
      {/* Spacer for player */}
      {isPlayerOpen && <div className="h-24 md:h-20" />}
    </main>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
